import { deltaToPlainText } from "util/richText";
import classNames from "classnames";
import { InvoiceSourceSyncBanner } from "components/InvoiceSourceSync/InvoiceSourceSyncBanner";
import { InvoiceSyncTag } from "components/InvoiceSourceSync/InvoiceSyncTag";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import CopyCode from "components/design/copyCode";
import { Divider } from "components/design/Divider";
import { FileIcon } from "components/design/FileIcon";
import Loader from "components/DesignSystem/Loader/loader";
import ToolTip from "components/design/toolTip";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Button } from "components/DesignSystem/Button/Button";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import { Link } from "components/DesignSystem/Link/Link";
import Modal from "components/DesignSystem/Modal/Modal";
import Slider from "components/DesignSystem/Slider/Slider";
import Tab from "components/DesignSystem/Tab/Tab";
import { AuditlogList } from "components/AuditlogList/AuditlogList";
import { DownloadInvoice } from "components/DownloadInvoice/DownloadInvoice";
import { FileInput } from "components/FileInput/FileInput";
import { LinkBreak } from "components/icons/LinkBreak";
import { LoadingIcon } from "components/icons/LoadingIcon";
import { SmallArrowUpRight } from "components/icons/SmallArrowUpRight";
import { TriangleWarning } from "components/icons/TriangleWarning";
import { TransactionSlider } from "components/Transaction/Slider/TransactionSlider";
import { LinkedJournalEntry } from "components/JournalEntry/LinkedJournalEntry";
import { CreditNoteSlider } from "pages/Books/CreditNotes/CreditNotesSlider";
import { UnlinkTransactionModal } from "components/UnlinkTransactionModal/UnlinkTransactionModal";
import {
  INVOICE_STATUSES,
  WRITTEN_OFF_UNLINK_DISABLED_TOOLTIP,
} from "constants/invoicing";
import { MANUAL_INVOICE_SOURCES } from "constants/revenueDataSource";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useDocPreview } from "hooks/useDocPreview";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { LockedUi } from "components/LockedUi/LockedUi";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { useModal } from "hooks/useModal";

import { useToast } from "hooks/useToast";
import { InvoiceStatus } from "pages/Books/Invoicing/InvoiceFullPageView";
import { MouseEvent, useState } from "react";
import { useHistory } from "react-router-dom";
import CopyPurple from "static/images/CopyPurple.svg";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import {
  useGetEntityInvoiceQuery,
  useUnlinkTransactionsMutation,
} from "store/apis/invoices";
import { useUnlinkCreditNoteMutation } from "store/apis/creditNotes";
import { openSlider } from "store/slices/transactions";
import { BackendError } from "types/utils/error";
import { formatDate } from "utils/formatDate";
import { AllItemScheduleDetails } from "./ItemScheduleDetails";
import dayjs from "dayjs";
import { DISPLAY_DATE } from "constants/date";
import { ExchangeRateInfoRow } from "components/design/ExchangeRateInfoRow";
import { USD } from "constants/currency";

const TransactionUnlinkButton = ({
  transactionId,
  invoiceId,
  financialRecordType,
  isWrittenOff,
}: {
  transactionId: string;
  invoiceId: string;
  financialRecordType: "BILL" | "INVOICE";
  isWrittenOff: boolean;
}) => {
  const unlinkModal = useModal();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.REVENUE
  );

  if (!hasWritePermission) return null;

  return (
    <>
      <ToolTip
        text={
          isWrittenOff
            ? WRITTEN_OFF_UNLINK_DISABLED_TOOLTIP
            : "Unlink Transaction"
        }
      >
        <span>
          <Button
            customType="ghost_icon"
            className="all:unset t-caret-text-30"
            disabled={isWrittenOff}
            aria-label="Unlink transaction"
            onClick={(e) => {
              e.stopPropagation();
              unlinkModal.open();
            }}
          >
            <LinkBreak />
          </Button>
        </span>
      </ToolTip>

      {unlinkModal.isOpen && (
        <UnlinkTransactionModal
          isOpen={unlinkModal.isOpen}
          onClose={unlinkModal.close}
          invoiceId={invoiceId}
          transactionId={transactionId}
          financialRecordType={financialRecordType}
        />
      )}
    </>
  );
};

const CreditNoteUnlinkButton = ({
  creditNoteId,
  invoiceId,
  isWrittenOff,
}: {
  creditNoteId: string;
  invoiceId: string;
  isWrittenOff: boolean;
}) => {
  const unlinkModal = useModal();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.REVENUE
  );
  const { alertToast, successToast } = useToast();
  const [unlinkCreditNote, { isLoading }] = useUnlinkCreditNoteMutation();

  const handleUnlink = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    try {
      await unlinkCreditNote({
        groupId,
        entityId,
        invoiceId,
        creditNoteId,
      }).unwrap();
      successToast({ message: "Credit note unlinked successfully" });
      unlinkModal.close();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ||
          "Failed to unlink credit note",
      });
    }
  };

  if (!hasWritePermission) return null;

  return (
    <>
      <ToolTip
        text={
          isWrittenOff
            ? WRITTEN_OFF_UNLINK_DISABLED_TOOLTIP
            : "Unlink Credit Note"
        }
      >
        <span>
          <Button
            customType="ghost_icon"
            className="all:unset t-caret-text-30"
            disabled={isWrittenOff}
            aria-label="Unlink credit note"
            onClick={(e) => {
              e.stopPropagation();
              unlinkModal.open();
            }}
          >
            <LinkBreak />
          </Button>
        </span>
      </ToolTip>

      {unlinkModal.isOpen && (
        <Modal.Root open={unlinkModal.isOpen} onOpenChange={unlinkModal.close}>
          <Modal.Content size="small" onClick={(e) => e.stopPropagation()}>
            <Modal.Body>
              <div>
                <span className="t-text-orange t-flex t-items-center t-justify-center t-w-10 t-h-10 t-rounded-full t-shadow-[0px_0px_0px_4px_rgba(255,220,78,0.15)] t-border t-border-orange-20 t-border-solid t-bg-yellow-20">
                  <TriangleWarning />
                </span>
                <p className="t-mb-2 t-text-subtext t-mt-5">
                  Unlink Credit Note?
                </p>
                <div className="t-text-body-sm t-text-text-30">
                  Unlinking the credit note will update the due balance for this
                  invoice.
                </div>
              </div>
            </Modal.Body>
            <Modal.FooterButtonGroup>
              <Modal.RawClose asChild>
                <Button>Cancel</Button>
              </Modal.RawClose>
              <Button
                isLoading={isLoading}
                disabled={isLoading}
                customType="primary"
                onClick={handleUnlink}
              >
                Confirm
              </Button>
            </Modal.FooterButtonGroup>
          </Modal.Content>
        </Modal.Root>
      )}
    </>
  );
};

export const InvoiceSlider = ({
  invoiceId,
  redirectLink,
}: {
  invoiceId: string;
  redirectLink: string;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const history = useHistory();
  const openPreview = useDocPreview();
  const dispatch = useAppDispatch();
  const { alertToast, successToast } = useToast();
  const [unlinkedTransaction, { isLoading: unlinkingTransaction }] =
    useUnlinkTransactionsMutation();
  const [selectedCreditNoteId, setSelectedCreditNoteId] = useState<
    string | null
  >(null);

  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );

  const { data: invoice, isLoading } = useGetEntityInvoiceQuery(
    {
      groupId: groupId,
      entityId: entityId,
      invoiceId,
    },
    { skip: !groupId || !entityId }
  );

  const isManualInvoice = Boolean(
    invoice?.source && MANUAL_INVOICE_SOURCES.includes(invoice.source)
  );
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.REVENUE
  );
  const { customer, associated_txns, paid_amount, total_revenue_recognised } =
    invoice || {};

  const isWrittenOff = Boolean(invoice?.written_off_amount);
  const reversalLedgerEntryIds =
    invoice?.reversal_ledger_entries?.map(({ uuid }) => uuid) ?? [];
  const ledgerEntryIds = [
    invoice?.ledger_entry?.uuid,
    invoice?.write_off_ledger_entry?.uuid,
    ...reversalLedgerEntryIds,
  ].filter((ledgerEntryId): ledgerEntryId is string => Boolean(ledgerEntryId));

  const exchangeRate = invoice?.payment_record_amount?.exchange_rate;
  const usdEquivalent = exchangeRate
    ? Number(invoice?.due_balance) / Number(exchangeRate)
    : null;

  const onEditInvoice = (e: MouseEvent<HTMLButtonElement>) => {
    const canEditFlow =
      hasWritePermission &&
      (invoice?.status === INVOICE_STATUSES.DUE ||
        invoice?.status === INVOICE_STATUSES.OVERDUE);

    if (canEditFlow) {
      history.push(`${redirectLink}&editflow=true&edit_from=slider`);
    } else {
      history.push(`${redirectLink}`);
    }
    e.stopPropagation();
  };

  const unLinkTransactionModal = useModal();

  const confirmUnlinkTransactions = async () => {
    if (!invoiceId || !invoice) return;
    try {
      await unlinkedTransaction({
        groupId,
        entityId,
        invoiceId,
        financial_record_type: invoice?.financial_record_type,
      }).unwrap();
      successToast({
        message: "Transactions unlinked successfully",
      });
      unLinkTransactionModal.close();
    } catch (error) {
      alertToast({
        message:
          (error as BackendError)?.data?.error?.message ||
          "Failed to unlink transactions",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="t-w-full t-h-full t-justify-center t-align-middle">
        <Loader />
      </div>
    );
  }

  if (!invoice) {
    return null;
  }

  return (
    <>
      <Slider.Header>
        <Slider.Title className="align-center t-text-subtitle t-flex t-items-center t-gap-2">
          {invoice?.title || invoice?.invoice_number}
          <span className="t-flex t-text-body-sm">
            <InvoiceStatus
              invoiceStatus={invoice?.status}
              writtenOffAmount={invoice?.written_off_amount}
              writtenOffDate={invoice?.written_off_at}
            />
          </span>
          <InvoiceSyncTag sourceSync={invoice.source_sync} />
        </Slider.Title>

        <Slider.Close />
      </Slider.Header>
      <Tab.Root
        defaultValue="DETAILS"
        className="t-h-full t-flex t-flex-col t-min-h-0"
      >
        <div className="t-px-5 t-pt-3 t-pb-4 t-flex-shrink-0">
          <Tab.List>
            <Tab.Trigger value="DETAILS">Details</Tab.Trigger>
            <Tab.Trigger value="ACTIVITY">Activity</Tab.Trigger>
          </Tab.List>
        </div>
        <Tab.Content
          value="DETAILS"
          className="t-flex t-flex-col t-flex-1 t-min-h-0 t-overflow-hidden"
        >
          <div className="t-flex t-flex-col t-gap-4 t-overflow-auto t-px-5 t-pb-5 t-h-full">
            <InvoiceSourceSyncBanner invoice={invoice} />
            <Accordion.Root
              type="multiple"
              defaultValue={[
                "INVOICE_DETAILS",
                "CUSTOMER_DETAILS",
                "ATTACHMENT",
                "LINKED_TXN",
                "TRANSACTION_DETAILS",
                "ITEM_SCHEDULE_DETAILS",
                "LINKED_CREDIT_NOTES",
              ]}
              className="t-flex t-flex-col t-gap-4"
            >
              <Accordion.Item value="INVOICE_DETAILS">
                <Accordion.Trigger className="!t-text-body-sm !t-font-medium">
                  Invoice Details
                </Accordion.Trigger>
                <Accordion.Content className="t-flex t-flex-col t-gap-6">
                  <div className="t-grid t-grid-cols-2 t-gap-y-6 t-gap-x-20 sm:t-gap-x-4">
                    <InfoItem label="To">
                      {invoice?.customer?.company_name || "-"}
                    </InfoItem>
                    <InfoItem label="Invoice Date">
                      {invoice?.invoice_date
                        ? formatDate(invoice?.invoice_date)
                        : "-"}
                    </InfoItem>
                    <InfoItem label="Due Date">
                      {invoice?.due_date ? formatDate(invoice?.due_date) : "-"}
                    </InfoItem>
                    {invoice?.currency && invoice.currency !== USD && (
                      <ExchangeRateInfoRow
                        rate={exchangeRate}
                        currency={invoice.currency}
                        date={invoice.invoice_date}
                        isCustom={invoice.has_exchange_rate_changed}
                        usdEquivalent={usdEquivalent}
                      />
                    )}
                    {invoice?.marked_void_at && (
                      <InfoItem label="Void Date">
                        {formatDate(invoice?.marked_void_at)}
                      </InfoItem>
                    )}
                    {invoice?.marked_uncollectible_at && (
                      <InfoItem label="Uncollectible Date">
                        {formatDate(invoice?.marked_uncollectible_at)}
                      </InfoItem>
                    )}
                    {isWrittenOff && (
                      <>
                        <InfoItem label="Written off as Bad Debt">
                          <AmountSuperScript
                            amount={Number(invoice.written_off_amount)}
                          />
                        </InfoItem>
                        <InfoItem label="Write-off Date">
                          {formatDate(invoice.written_off_at)}
                        </InfoItem>
                      </>
                    )}
                    <InfoItem label="Advance Paid">
                      <AmountSuperScript amount={Number(paid_amount) || 0} />
                    </InfoItem>
                    <>
                      {bookkeepingSettings?.deferred_revenue_enabled ? (
                        <InfoItem label="Total recognised amount">
                          <AmountSuperScript
                            amount={Number(total_revenue_recognised) || 0}
                          />
                        </InfoItem>
                      ) : (
                        <InfoItem label="Payment Link">
                          {invoice?.payment_link ? (
                            <div className="t-flex t-items-center t-gap-2">
                              <Link to={invoice?.payment_link}>
                                Redirect to Link
                              </Link>
                              <CopyCode
                                getCopyText={() => invoice?.payment_link}
                                copyIcon={CopyPurple}
                                addClassName="all:unset"
                                defaultText=" "
                                codeCopiedText="Link Copied"
                              />
                            </div>
                          ) : (
                            "-"
                          )}
                        </InfoItem>
                      )}
                    </>
                    {bookkeepingSettings?.deferred_revenue_enabled &&
                      invoice?.payment_link && (
                        <InfoItem label="Payment Link">
                          <div className="t-flex t-items-center t-gap-2">
                            <Link to={invoice?.payment_link}>
                              Redirect to Link
                            </Link>
                            <CopyCode
                              getCopyText={() => invoice?.payment_link}
                              copyIcon={CopyPurple}
                              addClassName="all:unset"
                              defaultText=" "
                              codeCopiedText="Link Copied"
                            />
                          </div>
                        </InfoItem>
                      )}
                    <InfoItem
                      label="Payment Instructions"
                      className="sm:t-col-span-2"
                    >
                      {deltaToPlainText(invoice?.entity?.payment_info) || "-"}
                    </InfoItem>
                  </div>
                </Accordion.Content>
              </Accordion.Item>
              {invoice.invoice_items.some(
                (item) => (item.revenue_schedule?.length || 0) > 0
              ) && <AllItemScheduleDetails invoice={invoice} />}
              <Accordion.Item value="CUSTOMER_DETAILS">
                <Accordion.Trigger className="!t-text-body-sm !t-font-medium">
                  Customer Details
                </Accordion.Trigger>
                <Accordion.Content className="t-flex t-flex-col t-gap-6">
                  <div className="t-grid t-grid-cols-2">
                    <Accordion.ItemGrid>
                      <InfoItem label="Name">
                        {customer?.company_name || "-"}
                      </InfoItem>
                    </Accordion.ItemGrid>
                    <Accordion.ItemGrid>
                      <InfoItem label="Contact">
                        {customer?.phone || "-"}
                      </InfoItem>
                    </Accordion.ItemGrid>
                  </div>
                  <Accordion.ItemGrid>
                    <InfoItem label="Email ID">
                      {customer?.email ? (
                        <CopyCode
                          getCopyText={() => customer?.email || ""}
                          defaultText={customer?.email}
                          copyIcon={CopyPurple}
                          /* @tw */
                          addClassName="all:unset"
                          codeCopiedText="Copied"
                        />
                      ) : customer?.recipient_emails &&
                        customer.recipient_emails.length > 0 ? (
                        <div className="t-flex t-flex-col t-gap-1">
                          {customer.recipient_emails.map((email) => (
                            <CopyCode
                              key={email}
                              getCopyText={() => email}
                              defaultText={email}
                              copyIcon={CopyPurple}
                              /* @tw */
                              addClassName="all:unset"
                              codeCopiedText="Copied"
                            />
                          ))}
                        </div>
                      ) : (
                        "-"
                      )}
                    </InfoItem>
                  </Accordion.ItemGrid>
                  <Accordion.ItemGrid>
                    <InfoItem label="Address">
                      {customer?.billing_address?.address_string || "-"}
                    </InfoItem>
                  </Accordion.ItemGrid>
                </Accordion.Content>
              </Accordion.Item>

              {associated_txns && associated_txns?.length > 0 && (
                <>
                  <Accordion.Item value="TRANSACTION_DETAILS">
                    <Accordion.Trigger className="!t-text-body-sm !t-font-medium">
                      <div className="t-flex t-justify-between t-w-full">
                        <span>Linked Transactions</span>
                      </div>
                      <LockedUi feature={WRITE_PERMISSION_KEYS.REVENUE}>
                        {isWrittenOff ? (
                          <ToolTip text={WRITTEN_OFF_UNLINK_DISABLED_TOOLTIP}>
                            <span className="t-flex t-gap-1.5 t-text-neutral-30 t-items-center t-text-button-sm t-cursor-not-allowed t-w-20">
                              Unlink all
                            </span>
                          </ToolTip>
                        ) : (
                          <div
                            className="t-flex t-gap-1.5 t-text-purple t-items-center t-text-button-sm t-cursor-pointer t-w-20"
                            onClick={(e) => {
                              e.stopPropagation();
                              unLinkTransactionModal.open();
                            }}
                          >
                            Unlink all
                          </div>
                        )}
                      </LockedUi>
                    </Accordion.Trigger>
                    <Accordion.Content className="t-flex t-flex-col t-gap-6">
                      {associated_txns?.map(
                        ({ amount, date, uuid, source }) => (
                          <button
                            className="t-text-subtext all:unset sm:t-block sm:t-w-full"
                            key={uuid}
                            onClick={() => dispatch(openSlider(uuid))}
                          >
                            <div className="t-grid t-grid-cols-6 t-cursor-pointer sm:t-flex sm:t-items-center sm:t-justify-between sm:t-gap-3">
                              <div className="t-col-span-4 t-flex t-gap-2 t-items-center sm:t-min-w-0 sm:t-flex-1">
                                <Avatar
                                  src={""}
                                  alt={customer?.company_name || ""}
                                />
                                <div>
                                  <div className="t-truncate t-max-w-40 sm:t-max-w-28">
                                    {customer?.company_name}
                                  </div>
                                  <div className="t-flex t-gap-2 t-text-body-sm t-text-text-30 ">
                                    <div className="t-text-body-sm t-text-text-30">
                                      {formatDate(date)}
                                    </div>{" "}
                                    <span className="t-text-neutral-20">•</span>
                                    <div className="first-letter:t-uppercase t-lowercase">
                                      {source}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="t-col-span-2 t-justify-self-end t-flex t-justify-end sm:t-shrink-0">
                                <div className="t-text-subtext t-flex t-gap-4 t-items-center sm:t-gap-2">
                                  <AmountSuperScript amount={Number(amount)} />
                                  <Divider fullHeight orientation="vertical" />
                                  <TransactionUnlinkButton
                                    transactionId={uuid}
                                    invoiceId={invoiceId}
                                    isWrittenOff={isWrittenOff}
                                    financialRecordType={
                                      invoice.financial_record_type as
                                        | "BILL"
                                        | "INVOICE"
                                    }
                                  />
                                  <span className="t-flex t-text-text-30">
                                    <SmallArrowUpRight />
                                  </span>
                                </div>
                              </div>
                            </div>
                          </button>
                        )
                      )}
                    </Accordion.Content>
                  </Accordion.Item>
                  <Modal.Root
                    open={unLinkTransactionModal.isOpen}
                    onOpenChange={unLinkTransactionModal.close}
                  >
                    <Modal.Content size="small">
                      <Modal.Body>
                        <div>
                          <span className="t-text-orange t-flex t-items-center t-justify-center t-w-10 t-h-10 t-rounded-full t-shadow-[0px_0px_0px_4px_rgba(255,220,78,0.15)] t-border t-border-orange-20 t-border-solid t-bg-yellow-20">
                            <TriangleWarning />
                          </span>
                          <p className="t-mb-2 t-text-subtext t-mt-5">
                            Unlink all transactions?
                          </p>
                          <div className="t-text-body-sm t-text-text-30">
                            Unlinking the transactions may change the invoice
                            status and will update the corresponding
                            transaction's categorisation.
                          </div>
                        </div>
                      </Modal.Body>
                      <Modal.FooterButtonGroup>
                        <Modal.RawClose asChild>
                          <Button>Cancel</Button>
                        </Modal.RawClose>
                        <Button
                          isLoading={unlinkingTransaction}
                          disabled={unlinkingTransaction}
                          customType="primary"
                          onClick={confirmUnlinkTransactions}
                        >
                          Confirm
                        </Button>
                      </Modal.FooterButtonGroup>
                    </Modal.Content>
                  </Modal.Root>
                </>
              )}

              {invoice?.linked_credit_debit_notes &&
                invoice.linked_credit_debit_notes.length > 0 && (
                  <Accordion.Item value="LINKED_CREDIT_NOTES">
                    <Accordion.Trigger className="!t-text-body-sm !t-font-medium">
                      Linked Credit notes
                    </Accordion.Trigger>
                    <Accordion.Content>
                      <div className="t-flex t-flex-col">
                        <div className="t-grid t-grid-cols-4 t-gap-2 t-py-2 t-border-0 t-border-b t-border-solid t-border-neutral-0">
                          <div className="t-text-body-sm t-text-text-30 t-flex t-items-center t-gap-1">
                            ID
                          </div>
                          <div className="t-text-body-sm t-text-text-30">
                            Date
                          </div>
                          <div className="t-text-body-sm t-text-text-30 t-text-right">
                            Credit Amount
                          </div>
                          <div />
                        </div>
                        {invoice.linked_credit_debit_notes.map((note) => (
                          <button
                            key={note.uuid}
                            className="all:unset t-w-full"
                            onClick={() => setSelectedCreditNoteId(note.uuid)}
                          >
                            <div className="t-grid t-grid-cols-4 t-gap-2 t-py-3 t-border-0 t-border-b t-border-solid t-border-neutral-0 t-cursor-pointer hover:t-bg-surface-lighter-grey t-items-center">
                              <div className="t-text-body-sm t-text-text-60">
                                {note.note_id}
                              </div>
                              <div className="t-text-body-sm t-text-text-100">
                                {dayjs(note.date).format(DISPLAY_DATE)}
                              </div>
                              <div className="t-text-body-sm t-text-text-100 t-text-right">
                                <AmountSuperScript amount={note.amount} />
                              </div>
                              <div className="t-flex t-items-center t-justify-end t-gap-2">
                                <CreditNoteUnlinkButton
                                  creditNoteId={note.uuid}
                                  invoiceId={invoiceId}
                                  isWrittenOff={isWrittenOff}
                                />
                                <span className="t-text-text-30">
                                  <SmallArrowUpRight />
                                </span>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </Accordion.Content>
                  </Accordion.Item>
                )}

              {invoice && (
                <Accordion.Item value="ATTACHMENT">
                  <Accordion.Trigger
                    disabled
                    className="!t-text-body-sm !t-font-medium"
                  >
                    Attachment
                  </Accordion.Trigger>
                  <Accordion.Content forceMount>
                    <div className="t-flex t-gap-2 t-flex-col">
                      <DownloadInvoice
                        invoice={invoice}
                        trigger={(isLoading, onClick) => (
                          <button
                            type="button"
                            className="t-w-full t-border-solid t-flex t-justify-between t-items-center t-bg-surface hover:t-bg-surface-lighter-grey t-border-neutral-10 t-gap-2 t-px-3 t-py-2 t-rounded t-border t-text-body-sm hover:t-border-purple-30"
                            onClick={onClick}
                          >
                            <div className="t-flex t-gap-1 t-items-center t-w-full">
                              <FileIcon fileType="PDF" />
                              <span>{invoice.invoice_number}.pdf</span>
                              <div className="t-flex t-items-center t-ml-auto">
                                {isLoading && (
                                  <span
                                    className={classNames(
                                      "t-flex t-origin-center t-animate-spin"
                                    )}
                                  >
                                    <LoadingIcon />
                                  </span>
                                )}
                              </div>
                            </div>
                          </button>
                        )}
                      />

                      {invoice.attachments &&
                        invoice.attachments.map((attachment) => (
                          <FileInput
                            key={attachment.uuid}
                            file={attachment}
                            groupId={groupId}
                            onFileClick={({ e, uuid: fileId }) =>
                              openPreview(fileId)
                            }
                          />
                        ))}
                    </div>
                  </Accordion.Content>
                </Accordion.Item>
              )}
            </Accordion.Root>
            <LinkedJournalEntry
              ledgerEntryIds={ledgerEntryIds}
              entityId={entityId}
            />
          </div>
          {isManualInvoice && (
            <Slider.FooterButtonGroup>
              <Button customType="primary" size="small" onClick={onEditInvoice}>
                {hasWritePermission &&
                [
                  INVOICE_STATUSES.DUE.toString(),
                  INVOICE_STATUSES.OVERDUE.toString(),
                  INVOICE_STATUSES.DRAFT.toString(),
                ].includes(invoice?.status)
                  ? "Edit Invoice"
                  : "View Invoice"}
              </Button>
            </Slider.FooterButtonGroup>
          )}
        </Tab.Content>
        <Tab.Content
          value="ACTIVITY"
          className="t-flex-1 t-min-h-0 t-overflow-hidden"
        >
          <AuditlogList entityId={entityId} objectId={invoiceId} />
        </Tab.Content>
      </Tab.Root>
      <TransactionSlider />
      {selectedCreditNoteId && invoice?.customer_id && (
        <Slider.Root
          open={Boolean(selectedCreditNoteId)}
          onOpenChange={() => setSelectedCreditNoteId(null)}
        >
          <Slider.Content open={Boolean(selectedCreditNoteId)}>
            <CreditNoteSlider
              creditNoteId={selectedCreditNoteId}
              customerId={invoice.customer_id}
            />
          </Slider.Content>
        </Slider.Root>
      )}
    </>
  );
};
